import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { formatDateAsMonthYear } from '@/utils/dateFormatter';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'asset_timetable_table';

export const ASSET_TIMETABLE_QUERY = gql`
  query assetTimetable($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      firstProductionDate
      peakProductionDate
      licenseExpirationDate
    }
  }
`;

interface AssetTimetableData {
  firstProductionDate: string;
  peakProductionDate: string;
  licenseExpirationDate: string;
}

const formatAssetTimetable = (asset: AssetTimetableData): AssetTimetableData => {
  return {
    firstProductionDate: formatDateAsMonthYear(asset.firstProductionDate, 'N/A'),
    peakProductionDate: formatDateAsMonthYear(asset.peakProductionDate, 'N/A'),
    licenseExpirationDate: formatDateAsMonthYear(asset.licenseExpirationDate, 'N/A'),
  };
};

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<AssetTimetableData>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const { loading, error } = useQuery<{ assetV2: Asset }>(ASSET_TIMETABLE_QUERY, {
    variables: { input: { id: assetId } },
    skip: !!persistedData || !assetId,
    onCompleted: response => {
      if (response?.assetV2) {
        const { licenseExpirationDate, firstProductionDate, peakProductionDate } =
          response.assetV2 || {};
        const data = { licenseExpirationDate, firstProductionDate, peakProductionDate };
        setDataFetched(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<AssetTimetableData>) => {
  if (!data) return null;
  const formattedData = formatAssetTimetable(data);

  const rows = [
    { label: 'First Production (Base Case)', value: formattedData.firstProductionDate },
    { label: 'Peak Production (Base Case)', value: formattedData.peakProductionDate },
    { label: 'License Expiration', value: formattedData.licenseExpirationDate },
  ];

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
      data-testid='asset-timetable-table'
    >
      <ComponentTable>
        <ComponentTableHeader>Asset Timetable</ComponentTableHeader>
        <ComponentTableBody rows={rows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetTimetableData>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <ComponentView data={data} />}
    </LoadingErrorWrapper>
  );
};

export const AssetTimeTableComponent: ComponentDefinition<AssetTimetableData> = {
  id: COMPONENT_ID,
  name: 'Asset Timetable',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
